/**
 * The shipped thread, as a renderer (RFC 0284 §4b, S6).
 *
 * `AgentPanel` is unchanged. What changes is that `WorldShell` stops naming it:
 * a session leaf now resolves its renderer the way a document leaf resolves its
 * editor, and the answer happens to be built in.
 *
 * NOT A SLOT, deliberately, and this file is where that decision is visible. A
 * session is already a leaf in the content tree: splittable, repeatable,
 * addressable in the url, sized in fractions. Giving it a slot as well would be
 * a second mechanism for a thing the tree already models, and would make "two
 * conversations side by side" mean something different from "two documents side
 * by side" for no reason anyone could name.
 *
 * WHAT DOES NOT TRAVEL WITH IT. The composer. A renderer is a VIEW of a
 * session, and submitting a turn is not a view (§4c). A bad third-party thread
 * renderer should look like a bad app; a bad third-party composer would look
 * like Mod is broken.
 */

import { AgentPanel } from '../../../features/thread/AgentPanel'
import type { BuiltinRenderer } from '../renderers'

/**
 * What a session leaf hands its renderer.
 *
 * Derived from the component rather than restated, so the two cannot drift.
 * Note what is in here: `onSelect`, `onSplit`, `onClose`, `onShowSidebar` and
 * `onOpenDocument` are all commands on the WINDOW, which is `mod.window` (§3d)
 * again, arrived at from the other side. An installed thread app needs every
 * one of them, which is why S6's installed half waits on S2.
 */
export type AgentRendererProps = React.ComponentProps<typeof AgentPanel>

export const BUILTIN_AGENT_RENDERER_ID = 'mod.thread'

export const builtinAgentRenderer: BuiltinRenderer<'agent'> = {
  kind: 'agent',
  id: BUILTIN_AGENT_RENDERER_ID,
  title: 'Thread',
  render: (props) => <AgentPanel {...props} />,
}
